/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Universal Shared Types & Schemas for Admin AI Sandbox Playground (Task 11.2.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

/**
 * Request schema for executing a direct test inference in Admin AI Sandbox.
 */
export const AdminSandboxTestRequestSchema = z
  .object({
    modelCode: z
      .string()
      .min(1)
      .openapi({
        description: 'Unique model identifier to test (e.g., gpt-4o-mini, claude-3-5-sonnet, gemini-1.5-pro, deepseek-chat)',
        example: 'gpt-4o-mini',
      }),
    provider: z
      .string()
      .optional()
      .openapi({
        description: 'Optional upstream provider override (e.g., openai, anthropic, google-ai-studio, deepseek)',
        example: 'openai',
      }),
    prompt: z
      .string()
      .min(1)
      .openapi({
        description: 'The user prompt content to send for test completion',
        example: 'Explain how Cloudflare Workers achieve sub-millisecond cold starts.',
      }),
    systemPrompt: z
      .string()
      .optional()
      .openapi({
        description: 'Optional custom system prompt to test instruction adherence',
        example: 'You are an edge computing architect. Provide concise, technically accurate answers.',
      }),
    temperature: z
      .number()
      .min(0.0)
      .max(2.0)
      .default(0.7)
      .openapi({
        description: 'Sampling temperature for randomness (0.0 = deterministic, 2.0 = highly creative)',
        example: 0.7,
      }),
    maxTokens: z
      .number()
      .int()
      .min(1)
      .max(8192)
      .default(1024)
      .openapi({
        description: 'Maximum generation output tokens',
        example: 1024,
      }),
    customApiKey: z
      .string()
      .optional()
      .openapi({
        description: 'Optional custom raw API key for testing a new key directly without saving to KeyPool',
        example: 'sk-proj-test123456789',
      }),
    mockSimulation: z
      .boolean()
      .optional()
      .openapi({
        description: 'Optional flag to execute a deterministic mock simulation (for test environments or when upstream credentials are not configured)',
        example: false,
      }),
  })
  .openapi('AdminSandboxTestRequest');

export type AdminSandboxTestRequest = z.infer<typeof AdminSandboxTestRequestSchema>;

/**
 * Telemetry performance metrics recorded during an AI Sandbox inference run.
 */
export const AdminSandboxTelemetrySchema = z
  .object({
    model: z.string().openapi({ description: 'Model machine code tested' }),
    provider: z.string().openapi({ description: 'Resolved upstream provider' }),
    isDirectKey: z.boolean().openapi({ description: 'Whether a custom direct key was provided' }),
    ttftMs: z.number().openapi({ description: 'Time to First Token in milliseconds' }),
    durationMs: z.number().openapi({ description: 'Total end-to-end execution duration in milliseconds' }),
    inputTokens: z.number().openapi({ description: 'Calculated or reported input prompt tokens' }),
    outputTokens: z.number().openapi({ description: 'Generated completion tokens' }),
    totalTokens: z.number().openapi({ description: 'Total tokens used' }),
    tokensPerSecond: z.number().openapi({ description: 'Calculated generation throughput (tokens/sec)' }),
    creditMultiplier: z.number().openapi({ description: 'Model credit multiplier' }),
    estimatedCostCredits: z.number().openapi({ description: 'Calculated credit expenditure based on token usage' }),
    status: z.enum(['completed', 'error', 'rate_limited']).openapi({ description: 'Execution result status' }),
    gatewayCached: z.boolean().default(false).openapi({ description: 'Whether the response was served from Cloudflare AI Gateway cache' }),
  })
  .openapi('AdminSandboxTelemetry');

export type AdminSandboxTelemetry = z.infer<typeof AdminSandboxTelemetrySchema>;

/**
 * Non-streaming response envelope for direct test execution.
 */
export const AdminSandboxTestResponseSchema = z
  .object({
    output: z.string().openapi({ description: 'The complete generated text output' }),
    reasoning: z.string().nullable().optional().openapi({ description: 'Reasoning or chain-of-thought text if emitted' }),
    telemetry: AdminSandboxTelemetrySchema.openapi({ description: 'Performance and token telemetry' }),
  })
  .openapi('AdminSandboxTestResponse');

export type AdminSandboxTestResponse = z.infer<typeof AdminSandboxTestResponseSchema>;

/**
 * Pre-built prompt preset schema for quick testing.
 */
export const SandboxPresetSchema = z
  .object({
    id: z.string(),
    category: z.string(),
    title: z.string(),
    systemPrompt: z.string().optional(),
    prompt: z.string(),
    suggestedModel: z.string().optional(),
    temperature: z.number().optional(),
  })
  .openapi('SandboxPreset');

export type SandboxPreset = z.infer<typeof SandboxPresetSchema>;
